import { useNavigate } from 'react-router-dom'
import { Clock, Info, Plus } from 'lucide-react'

import EmptyCard from './EmptyCard'
import ShrinkToFit from './ShrinkToFit'

import type { ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const MINUTES_PER_HOUR = 60
const MINUTES_PER_DAY = 60 * 24
const MINUTES_PER_WEEK = MINUTES_PER_DAY * 7

const formatTimeSaved = (minutes: number): string => {
  if (minutes < MINUTES_PER_DAY) {
    const rounded = Math.round(minutes)
    return `${rounded.toLocaleString('en-US')} ${rounded === 1 ? 'minute' : 'minutes'}`
  }
  if (minutes < MINUTES_PER_WEEK) {
    const hours = Math.round(minutes / MINUTES_PER_HOUR)
    return `${hours.toLocaleString('en-US')} ${hours === 1 ? 'hour' : 'hours'}`
  }
  // Days: round to whole — the underlying WPM-based estimate is rough enough
  // that fractional days imply more precision than we actually have.
  const days = Math.round(minutes / MINUTES_PER_DAY)
  return `${days.toLocaleString('en-US')} ${days === 1 ? 'day' : 'days'}`
}

const formatSinceDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

interface TimeSavedCardProps {
  response: ImpactResponse['minutes_saved']
}

const TimeSavedCard = ({ response }: TimeSavedCardProps) => {
  const navigate = useNavigate()

  if (response.total === 0) {
    return (
      <EmptyCard
        icon={<Clock className="size-5" />}
        title="Time saved"
        description="Insights will appear once we have enough data to calculate this trend"
        cta={{
          label: 'Invite team members',
          icon: <Plus className="size-4" />,
          onClick: () => navigate('/app/team'),
        }}
      />
    )
  }

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">Time saved</CardTitle>
          <p className="flex gap-1.5 text-[15px] leading-5 text-muted-foreground">
            Based on your team&apos;s dictation speed vs. average typing speed
            <Tooltip>
              <TooltipTrigger
                aria-label="About time saved"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fathom)]"
              >
                <Info className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[320px] text-muted-foreground">
                We take your team&apos;s average dictation speed, compare it to an average typing speed of 40 WPM, and
                apply the difference to your total words dictated with Flow to estimate time saved.
              </TooltipContent>
            </Tooltip>
          </p>
        </div>
      </CardHeader>
      <CardContent className="flex h-[250px] flex-col items-center justify-center gap-2 px-6">
        <ShrinkToFit
          text={formatTimeSaved(response.total)}
          className="font-semibold leading-none tracking-tight text-foreground text-center"
        />
        <div className="text-[18px] leading-5 text-center">Since {formatSinceDate(response.since)}</div>
      </CardContent>
    </Card>
  )
}

export default TimeSavedCard
